import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Badge, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type ReturnRow = {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  status: 'requested' | 'approved' | 'rejected' | 'picked_up' | 'refunded' | 'closed';
  reason: string;
  note?: string | null;
  adminNote?: string | null;
  refundAmount?: number | null;
  createdAt?: string;
};

const statusBadge = (status: string) => {
  if (status === 'requested') return 'secondary';
  if (status === 'approved') return 'primary';
  if (status === 'picked_up') return 'info';
  if (status === 'refunded') return 'success';
  if (status === 'closed') return 'dark';
  return 'danger';
};

const Returns = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [noteById, setNoteById] = useState<Record<string, string>>({});

  const fetchReturns = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get('/returns', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 100,
          status: statusFilter === 'all' ? undefined : statusFilter,
        },
      });
      const list = Array.isArray(response.data?.returns) ? response.data.returns : [];
      setRows(list);
      setError('');
    } catch (err: any) {
      setRows([]);
      setError(err?.response?.data?.message || 'Failed to load returns.');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const updateStatus = async (row: ReturnRow, status: ReturnRow['status']) => {
    if (!token) return;
    try {
      await axios.put(
        `/returns/${row._id}/status`,
        {
          status,
          adminNote: noteById[row._id]?.trim() || undefined,
          refundAmount: status === 'refunded' ? Number(row.refundAmount || 0) : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(`Return ${row._id} updated to ${status}.`);
      await fetchReturns();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update return status.');
    }
  };

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Returns</h4>
        <Form.Select
          style={{ width: 220 }}
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="requested">Requested</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="picked_up">Picked up</option>
          <option value="refunded">Refunded</option>
          <option value="closed">Closed</option>
        </Form.Select>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {loading ? (
        <p className="mb-0">Loading returns...</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Admin Note</th>
              <th>Created</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">No return requests found.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row._id}>
                  <td>
                    <div><strong>{row.orderNumber}</strong></div>
                    <small className="text-muted">#{row._id}</small>
                  </td>
                  <td>
                    <div>{row.customerName}</div>
                    <small>{row.customerPhone}</small>
                  </td>
                  <td>
                    <div>{row.reason}</div>
                    {row.note ? <small className="text-muted">{row.note}</small> : null}
                  </td>
                  <td><Badge bg={statusBadge(row.status)}>{row.status}</Badge></td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={noteById[row._id] ?? row.adminNote ?? ''}
                      onChange={(event) => setNoteById((prev) => ({ ...prev, [row._id]: event.target.value }))}
                      placeholder="Optional admin note"
                    />
                  </td>
                  <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={row.status}
                      onChange={(event) => updateStatus(row, event.target.value as ReturnRow['status'])}
                    >
                      <option value="requested">requested</option>
                      <option value="approved">approved</option>
                      <option value="rejected">rejected</option>
                      <option value="picked_up">picked_up</option>
                      <option value="refunded">refunded</option>
                      <option value="closed">closed</option>
                    </Form.Select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </Card>
  );
};

export default Returns;
